import { useEffect, useState } from 'react';

/**
 * Resolve CSS custom properties to concrete values, and re-resolve when the
 * theme changes.
 *
 * A `<canvas>` cannot use `var(--x)` — `fillStyle` needs a real colour string.
 * Without this, every canvas on the site has to hardcode a palette, which is
 * exactly how the app ended up with surfaces that ignored the theme. Instruments
 * that draw imperatively read their palette through here instead.
 *
 *   const [ink, grid] = useThemeTokens('--bs-ink', '--bs-rule');
 */
export function useThemeTokens(...names: string[]): string[] {
    const [values, setValues] = useState<string[]>(() => names.map(() => ''));

    useEffect(() => {
        const read = () => {
            const style = getComputedStyle(document.documentElement);
            setValues(names.map(n => style.getPropertyValue(n).trim()));
        };

        read();

        // ThemeContext switches themes by setting data-theme on <html>, so that
        // attribute is the signal that the resolved values are now stale.
        const observer = new MutationObserver(read);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });

        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [names.join('|')]);

    return values;
}

/** Single-token convenience. */
export function useThemeToken(name: string, fallback = ''): string {
    const [value] = useThemeTokens(name);
    return value || fallback;
}
